import {Avatar, Image, Text, VStack} from "@chakra-ui/react";
import MessageBubble from "./MessageBubble";
import {useRef, useEffect, Fragment} from "react";
import {useChat} from "../../context/ChatContext";
import {MessageHeader} from "./MessageHeader";
import SendMessageInput from "./SendMessageInput";
import {CentralBubble, DateDivider, UnreadDivider} from "./MessageDivider";
import {scrollbar} from "../styling";
import {Participant, RequestAction} from "../../models";
import {stateChecker as check} from "../../helper/utils";

type ChatWindowProps = {
    archived?: boolean
    note?: string
    onAction: (v: RequestAction) => void
    participants: number
    title: string
}
const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};


function ChatWindow(p: ChatWindowProps) {
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const {actions, currentChat, handleSendMessage, state} = useChat();

    useEffect(() => {
        bottomRef.current?.scrollIntoView({behavior: "auto"});
    }, [currentChat]);

    useEffect(function () {
        let read: boolean = false;
        const observer = new IntersectionObserver(function (entries) {
            if (entries[0].isIntersecting && !read) {
                read = true;
                setTimeout(() => p.onAction({type: "read-messages"}), 2000);
            }
        }, {threshold: 1, rootMargin: "0px"});
        if (bottomRef.current) {
            observer.observe(bottomRef.current);
        }
        return function () {
            if (bottomRef.current) {
                observer.unobserve(bottomRef.current);
            }
        };
    }, []);

    return <VStack
        w="full"
        borderWidth={1}
        borderRadius={{base: 0, xl: "24px"}}
        bg={"white"}
        data-cy="chat-window"
        overflowY="hidden"
        alignItems="stretch"
        h={{base: "100dvb", xl: "auto"}}
    >
        {currentChat && <>
            <MessageHeader
                title={p.title}
                subtitle={<VStack flexDir="row" gap=".25rem" flexWrap="wrap" align="baseline">
                    <Text className="one-line" fontSize="xs" color="gray.600" textTransform="capitalize">
                        {Participant.summarize(p.participants)}
                    </Text>
                    <Text as="span" fontSize=".85em">(click here to view the details)</Text>
                </VStack>
                }
                onAction={p.onAction}
            />
            {currentChat.item.messages.length === 0 && <VStack
                color="dark.500"
                data-cy="empty-messages-placeholder"
                flex="2 1 auto"
                justifyContent="center"
            >
                <Image src="/images/chats.svg" w="90px" h="90px" />
                <Text textAlign="center" fontSize="md" color="black">
                    No messages sent in the chat for the moment.
                </Text>
            </VStack>}
            {currentChat.item.messages.length > 0 && <>
                <VStack
                    spacing={2}
                    align="stretch"
                    flex="2 1 auto"
                    overflowY="auto"
                    px={4}
                    py={3}
                    sx={scrollbar.styled}
                >
                    {currentChat.groupedMessages.groups.map(function (v) {
                        return <Fragment key={`${v.label.replaceAll(" ", "")}`}>
                            <DateDivider key={`date-divider-${v.items[0].id}`} label={v.label}/>
                            {v.items.map((msg) => <>
                                {msg.id === currentChat.groupedMessages.lastUnread && <UnreadDivider
                                    key={`message-divider-${msg.timestamp}`}
                                    count={currentChat.unread.length}
                                />}
                                <MessageBubble
                                    id={`message-item-${msg.id}`}
                                    content={msg.content}
                                    subtitle={formatTime(msg.timestamp)}
                                    sent={msg.sent}
                                    status={msg.status}
                                />
                                {msg.showSender && <VStack flexDir="row" align="baseline">
                                    <Avatar size="xs" name={msg.senderName}/>
                                    <Text
                                        fontSize=".85em"
                                        alignSelf="flex-start"
                                        textTransform="capitalize"
                                        color="primary.500"
                                        fontWeight="bold"
                                    >
                                        {msg.senderName}
                                    </Text>
                                </VStack>}
                            </>)}
                        </Fragment>;
                    })}
                    {p.note && <CentralBubble note={p.note}/>}
                    <div ref={bottomRef} />
                </VStack>
            </>}
            {!p.archived &&  <SendMessageInput
                currentValue={state.inputMessage}
                onUpdate={actions.setInputMessage}
                onSent={handleSendMessage}
                loading={check.isPending(state.loadingState)}
                failed={state.message !== undefined}
            />}
        </>}
        {!currentChat && <VStack flex="2" justifyContent="center" color="dark.500">
            <Image src="/images/chats.svg" w="90px" h="90px" />
            <Text textAlign="center" color="black" fontSize="lg">
                No chat selected. Please select one of the chat thread.
            </Text>
        </VStack>}
    </VStack>;
}

export default ChatWindow;
